import React, { useState } from 'react';
import { X, AlertTriangle, Send } from 'lucide-react';
import { Button } from './ui/button';
import { useToast } from '../hooks/use-toast';
import { useAuth } from '../context/AuthContext';
import { api, friendlyError } from '../lib/api';

const REASONS = [
  { value: 'precio-falso', label: 'Precio sospechoso o falso' },
  { value: 'fotos-falsas', label: 'Fotos no corresponden al inmueble' },
  { value: 'duplicada', label: 'Publicación duplicada' },
  { value: 'fraude', label: 'Posible fraude o estafa' },
  { value: 'datos-incorrectos', label: 'Información incorrecta' },
  { value: 'otro', label: 'Otro motivo' },
];

export default function ReportPropertyModal({ propertyId, onClose }) {
  const { toast } = useToast();
  const { user } = useAuth();
  const [reason, setReason] = useState('precio-falso');
  const [details, setDetails] = useState('');
  const [email, setEmail] = useState('');
  const [submitting, setSubmitting] = useState(false);

  const submit = async () => {
    if (!user && !email) {
      toast({ title: 'Email requerido', description: 'Necesitamos un correo para procesar tu reporte.', variant: 'destructive' });
      return;
    }
    setSubmitting(true);
    try {
      await api.reportProperty(propertyId, {
        property_id: propertyId,
        reason,
        details: details || undefined,
        reporter_email: user ? undefined : email,
      });
      toast({ title: '¡Reporte enviado!', description: 'Gracias por ayudarnos a mantener habita.co confiable.' });
      onClose();
    } catch (e) {
      toast({
        title: 'No pudimos enviar tu reporte',
        description: friendlyError(e, 'Intenta de nuevo en unos minutos.'),
        variant: 'destructive',
      });
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/55 backdrop-blur-sm z-50 flex items-end md:items-center justify-center p-0 md:p-4" onClick={onClose} data-testid="report-modal">
      <div className="bg-white w-full md:max-w-md rounded-t-3xl md:rounded-2xl overflow-hidden" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-start justify-between p-6 pb-4 border-b border-gray-100">
          <div className="flex items-start gap-3">
            <div className="w-10 h-10 rounded-xl bg-amber-50 flex items-center justify-center shrink-0">
              <AlertTriangle className="w-5 h-5 text-amber-600" />
            </div>
            <div>
              <h3 className="font-serif text-xl font-bold text-habita-dark">Reportar publicación</h3>
              <p className="text-xs text-gray-500 mt-0.5">Tu reporte es anónimo y nuestro equipo lo revisará en 24h.</p>
            </div>
          </div>
          <button onClick={onClose} className="w-8 h-8 rounded-full hover:bg-gray-100 flex items-center justify-center" data-testid="report-close-btn">
            <X className="w-4 h-4 text-gray-500" />
          </button>
        </div>

        <div className="p-6 space-y-4">
          <div>
            <label className="text-xs font-semibold uppercase tracking-widest text-gray-500">Motivo</label>
            <div className="mt-2 space-y-2">
              {REASONS.map((r) => (
                <label key={r.value} className={`flex items-center gap-3 p-3 rounded-xl border cursor-pointer transition-colors ${reason === r.value ? 'border-habita bg-habita-mint' : 'border-gray-200 hover:border-gray-300'}`}>
                  <input
                    type="radio"
                    name="reason"
                    value={r.value}
                    checked={reason === r.value}
                    onChange={(e) => setReason(e.target.value)}
                    className="accent-[#00824D]"
                    data-testid={`report-reason-${r.value}`}
                  />
                  <span className="text-sm font-medium text-gray-800">{r.label}</span>
                </label>
              ))}
            </div>
          </div>

          <div>
            <label className="text-xs font-semibold uppercase tracking-widest text-gray-500">Detalles (opcional)</label>
            <textarea
              value={details}
              onChange={(e) => setDetails(e.target.value)}
              rows={3}
              maxLength={500}
              placeholder="Cuéntanos más para ayudar a la revisión..."
              className="mt-2 w-full p-3 border border-gray-200 rounded-xl text-sm outline-none focus:border-habita resize-none"
              data-testid="report-details-input"
            />
          </div>

          {!user && (
            <div>
              <label className="text-xs font-semibold uppercase tracking-widest text-gray-500">Tu email</label>
              <input
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="ejemplo@correo.com"
                className="mt-2 w-full p-3 border border-gray-200 rounded-xl text-sm outline-none focus:border-habita"
                data-testid="report-email-input"
              />
            </div>
          )}

          <Button
            onClick={submit}
            disabled={submitting}
            className="btn-habita w-full h-12 rounded-full"
            data-testid="report-submit-btn"
          >
            <Send className="w-4 h-4 mr-2" />
            {submitting ? 'Enviando...' : 'Enviar reporte'}
          </Button>
        </div>
      </div>
    </div>
  );
}
